/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { Menu, MenuItem } from '../Menu/index.js';
import { useTheme, SvgCaretDownSmall, Popover, useId, AutoclearingHiddenLiveRegion, } from '../utils/index.js';
import '@itwin/itwinui-css/css/select.css';
import SelectTag from './SelectTag.js';
import SelectTagContainer from './SelectTagContainer.js';
const isMultipleEnabled = (variable, multiple) => {
    return multiple;
};
// Type guard for multiple did not work
const isSingleOnChange = (onChange, multiple) => {
    return !multiple;
};
/**
 * Select component to select value from options.
 * Generic type is used for value. It prevents you from mistakenly using other types in `options`, `value` and `onChange`.
 * @example
 * <caption>Basic select.</caption>
 * <Select<number> options={[{ value: 1, label: 'Option 1' }, { value: 2, label: 'Option 2' }, { value: 3, label: 'Option 3' }]} />
 * @example
 * <caption>Disabled select with placeholder.</caption>
 * <Select
 *   disabled={true}
 *   placeholder='Placeholder text'
 *   options={[{ value: 1, label: 'Option 1' }, { value: 2, label: 'Option 2' }, { value: 3, label: 'Option 3' }]}
 * />
 * @example
 * <caption>Select with selected value and change handler.</caption>
 * <Select<number>
 *   value={selectedValue}
 *   onChange={(value) => setSelectedValue(value)}
 *   options={[{ value: 1, label: 'Option 1' }, { value: 2, label: 'Option 2' }, { value: 3, label: 'Option 3' }]}
 * />
 * @example
 * <caption>Select using custom renderers for menu items and selected value.</caption>
 * <Select<string>
 *   options={[
 *     { value: 'yellow', label: 'Yellow' },
 *     { value: 'green', label: 'Green' },
 *     { value: 'red', label: 'Red' },
 *   ]}
 *   value={selectedValue}
 *   placeholder='Placeholder text'
 *   itemRenderer={(option, itemProps) => (
 *     <MenuItem
 *       style={{ color: option.value }}
 *       isSelected={itemProps.isSelected}
 *       onClick={() => {
 *         setSelectedValue(option.value);
 *         itemProps.close();
 *       }}
 *       role='option'
 *       ref={(el) => itemProps.isSelected && el?.scrollIntoView()}
 *     >
 *       {option.label}
 *     </MenuItem>
 *   )}
 *   selectedItemRenderer={(option) => (
 *     <span style={{ backgroundColor: option.value }}>{option.label}</span>
 *   )}
 * />
 */
export const Select = (props) => {
    var _a;
    const uid = useId();
    const { options, value, onChange, placeholder, disabled = false, size, setFocus = false, itemRenderer, selectedItemRenderer, className, style, menuClassName, menuStyle, onShow, onHide, popoverProps, multiple = false, triggerProps, ...rest } = props;
    useTheme();
    const [isOpenState, setIsOpen] = React.useState(false);
    const isOpen = (_a = popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.visible) !== null && _a !== void 0 ? _a : isOpenState;
    const [minWidth, setMinWidth] = React.useState(0);
    const [liveRegionSelection, setLiveRegionSelection] = React.useState('');
    const selectRef = React.useRef(null);
    const toggleButtonRef = React.useRef(null);
    const onShowHandler = React.useCallback((instance) => {
        setIsOpen(true);
        onShow === null || onShow === void 0 ? void 0 : onShow(instance);
    }, [onShow]);
    const onHideHandler = React.useCallback((instance) => {
        var _a;
        setIsOpen(false);
        (_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true }); // move focus back to select button
        onHide === null || onHide === void 0 ? void 0 : onHide(instance);
    }, [onHide]);
    React.useEffect(() => {
        if (selectRef.current && !disabled && setFocus) {
            selectRef.current.focus();
        }
    }, [setFocus, disabled]);
    React.useEffect(() => {
        if (selectRef.current) {
            setMinWidth(selectRef.current.offsetWidth);
        }
    }, [isOpen]);
    const onKeyDown = (event) => {
        if (event.altKey) {
            return;
        }
        switch (event.key) {
            case 'Enter':
            case ' ':
            case 'Spacebar': {
                setIsOpen((o) => !o);
                event.preventDefault();
                break;
            }
            default:
                break;
        }
    };
    const menuItems = React.useMemo(() => {
        return options.map((option, index) => {
            var _a;
            const isSelected = isMultipleEnabled(value, multiple)
                ? (_a = value === null || value === void 0 ? void 0 : value.includes(option.value)) !== null && _a !== void 0 ? _a : false
                : value === option.value;
            const menuItem = itemRenderer ? (itemRenderer(option, { close: () => setIsOpen(false), isSelected })) : (React.createElement(MenuItem, null, option.label));
            const { label, ...restOption } = option;
            return React.cloneElement(menuItem, {
                key: `${label}-${index}`,
                isSelected,
                onClick: () => {
                    if (option.disabled) {
                        return;
                    }
                    if (isSingleOnChange(onChange, multiple)) {
                        onChange === null || onChange === void 0 ? void 0 : onChange(option.value);
                        setIsOpen(false);
                    }
                    else {
                        onChange === null || onChange === void 0 ? void 0 : onChange(option.value, isSelected ? 'removed' : 'added');
                    }
                    // update live region
                    if (isMultipleEnabled(value, multiple)) {
                        const prevSelectedValue = value || [];
                        const newSelectedValue = isSelected
                            ? prevSelectedValue.filter((i) => option.value !== i)
                            : [...prevSelectedValue, option.value];
                        setLiveRegionSelection(options
                            .filter((i) => newSelectedValue.includes(i.value))
                            .map((item) => item.label)
                            .filter(Boolean)
                            .join(', '));
                    }
                },
                ref: (el) => {
                    if (isSelected && !multiple) {
                        el === null || el === void 0 ? void 0 : el.scrollIntoView({ block: 'nearest' });
                    }
                },
                role: 'option',
                ...restOption,
                ...menuItem.props,
            });
        });
    }, [itemRenderer, multiple, onChange, options, value]);
    const selectedItems = React.useMemo(() => {
        if (value == null) {
            return undefined;
        }
        return isMultipleEnabled(value, multiple)
            ? options.filter((option) => value.some((val) => val === option.value))
            : options.find((option) => option.value === value);
    }, [multiple, options, value]);
    const tagRenderer = React.useCallback((item) => {
        return React.createElement(SelectTag, { key: item.label, label: item.label });
    }, []);
    return (React.createElement("div", { className: cx('iui-input-with-icon', className), style: style, ...rest },
        React.createElement(Popover, { content: React.createElement(Menu, { role: 'listbox', className: cx('iui-scroll', menuClassName), style: {
                    minWidth,
                    maxWidth: `min(${minWidth * 2}px, 90vw)`,
                    ...menuStyle,
                }, id: `${uid}-menu`, key: `${uid}-menu` }, menuItems), placement: 'bottom-start', aria: { content: null }, onShow: onShowHandler, onHide: onHideHandler, ...popoverProps, visible: isOpen, onClickOutside: (_, { target }) => {
                var _a;
                if (!((_a = toggleButtonRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))) {
                    setIsOpen(false);
                }
            } },
            React.createElement("div", { tabIndex: 0, role: 'combobox', ref: selectRef, "data-iui-size": size, onClick: () => !disabled && setIsOpen((o) => !o), onKeyDown: (e) => !disabled && onKeyDown(e), "aria-disabled": disabled, "aria-autocomplete": 'none', "aria-expanded": isOpen, "aria-haspopup": 'listbox', "aria-controls": `${uid}-menu`, ...triggerProps, className: cx('iui-select-button', {
                    'iui-placeholder': (!selectedItems || selectedItems.length === 0) && !!placeholder,
                    'iui-disabled': disabled,
                }, triggerProps === null || triggerProps === void 0 ? void 0 : triggerProps.className) },
                (!selectedItems || selectedItems.length === 0) && (React.createElement("span", { className: 'iui-content' }, placeholder)),
                isMultipleEnabled(selectedItems, multiple) ? (React.createElement(MultipleSelectButton, { selectedItems: selectedItems, selectedItemsRenderer: selectedItemRenderer, tagRenderer: tagRenderer })) : (React.createElement(SingleSelectButton, { selectedItem: selectedItems, selectedItemRenderer: selectedItemRenderer })))),
        React.createElement("span", { "aria-hidden": true, ref: toggleButtonRef, className: cx('iui-end-icon', {
                'iui-actionable': !disabled,
                'iui-disabled': disabled,
                'iui-open': isOpen,
            }), onClick: () => !disabled && setIsOpen((o) => !o) },
            React.createElement(SvgCaretDownSmall, null)),
        multiple ? (React.createElement(AutoclearingHiddenLiveRegion, { text: liveRegionSelection })) : null));
};
const SingleSelectButton = ({ selectedItem, selectedItemRenderer, }) => {
    return (React.createElement(React.Fragment, null,
        selectedItem &&
            selectedItemRenderer &&
            selectedItemRenderer(selectedItem),
        selectedItem && !selectedItemRenderer && (React.createElement(React.Fragment, null,
            selectedItem.icon &&
                React.cloneElement(selectedItem.icon, {
                    className: cx(selectedItem.icon.props.className, 'iui-icon'),
                }),
            React.createElement("span", { className: 'iui-content' }, selectedItem.label)))));
};
const MultipleSelectButton = ({ selectedItems, selectedItemsRenderer, tagRenderer, }) => {
    const selectedItemsElements = React.useMemo(() => {
        if (!selectedItems) {
            return [];
        }
        return selectedItems.map((item) => tagRenderer(item));
    }, [selectedItems, tagRenderer]);
    return (React.createElement(React.Fragment, null,
        selectedItems &&
            selectedItemsRenderer &&
            selectedItemsRenderer(selectedItems),
        selectedItems && !selectedItemsRenderer && (React.createElement("span", { className: 'iui-content' },
            React.createElement(SelectTagContainer, { tags: selectedItemsElements })))));
};
export default Select;
